/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { SkillShopService } from '../services/skillShopService';
import type { WalkthroughState as StoredWalkthroughState, WalkthroughPrerequisite } from '@shared/types/product-app-settings';
import type { IntegrationSettings } from '@shared/types/user_settings';

export interface WalkthroughStep {
  id: string;
  targetSelector: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  action?: 'click' | 'observe' | 'navigate';
  navigateTo?: string;
  highlight?: boolean;
  agentPrompt?: string; // Suggested prompt for the agent sidebar
}

export interface PrerequisiteCheck {
  id: string;
  label: string;
  description: string;
  isRequired: boolean;
  isCompleted: boolean;
}

interface WalkthroughState {
  isActive: boolean;
  currentStepIndex: number;
  showPrerequisites: boolean;
  prerequisitesCompleted: boolean;
  prerequisites: PrerequisiteCheck[];
  hasCompletedWalkthrough: boolean;
}

interface WalkthroughContextType {
  state: WalkthroughState;
  steps: WalkthroughStep[];
  currentStep: WalkthroughStep | null;
  isInitialized: boolean;
  startWalkthrough: () => void;
  endWalkthrough: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (index: number) => void;
  skipWalkthrough: () => void;
  showPrerequisitesDialog: () => void;
  hidePrerequisitesDialog: () => void;
  updatePrerequisite: (id: string, isCompleted: boolean) => void;
  completePrerequisites: () => void;
  resetWalkthrough: () => void;
  triggerFirstVisitCheck: () => void;
}

// Define the walkthrough steps
const WALKTHROUGH_STEPS: WalkthroughStep[] = [
  // Introduction to sidebar navigation
  {
    id: 'sidebar-intro',
    targetSelector: '[data-walkthrough="sidebar-nav"]',
    title: 'Navigation Sidebar',
    content: 'This is your main navigation. From here, you can access all the key pages of Velocity: Dashboard, Products, Releases, User Stories, Issues, Changes, and Settings.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  {
    id: 'dashboard-link',
    targetSelector: '[data-walkthrough="nav-dashboard"]',
    title: 'Dashboard',
    content: 'The Dashboard gives you an overview of your product development progress, including activity metrics and velocity graphs.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  {
    id: 'products-link',
    targetSelector: '[data-walkthrough="nav-products"]',
    title: 'Products',
    content: 'Manage your product portfolio here. This is where you\'ll create and configure the products you\'re building.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  {
    id: 'releases-link',
    targetSelector: '[data-walkthrough="nav-releases"]',
    title: 'Releases',
    content: 'Track and plan your product releases. Organize work into releases to manage delivery timelines.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  {
    id: 'user-stories-link',
    targetSelector: '[data-walkthrough="nav-user-stories"]',
    title: 'User Stories',
    content: 'Define what you\'re building from the user\'s perspective. User stories are organized into epics for better management.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  {
    id: 'issues-link',
    targetSelector: '[data-walkthrough="nav-issues"]',
    title: 'Issues',
    content: 'Track bugs, tasks, and features. Issues are the building blocks of your development work.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  // Agent Sidebar
  {
    id: 'agent-sidebar',
    targetSelector: '[data-agent-sidebar-expand-button]',
    title: 'AI Agent Sidebar',
    content: 'This is your AI assistant! Click the expand button to open the agent sidebar. You can chat with specialized agents like the Product Manager to help with product development tasks.',
    placement: 'right',
    action: 'observe',
    highlight: true,
  },
  // Agent Chat History
  {
    id: 'agent-chat-history',
    targetSelector: '[data-agent-chat-expand-button]',
    title: 'AI Agent Chat History',
    content: 'This is the chat history panel where you can view and reload previous agent chats.',
    placement: 'left',
    action: 'observe',
    highlight: true,
  },
  // Navigate to Products page
  {
    id: 'go-to-products',
    targetSelector: '[data-walkthrough="nav-products"]',
    title: 'Let\'s Create a Product',
    content: 'Now let\'s create your first product! Click on "Products" to navigate to the Products page.',
    placement: 'right',
    action: 'navigate',
    navigateTo: '/products',
    highlight: true,
  },
  // Quick Product Request
  {
    id: 'quick-product-request',
    targetSelector: '[data-walkthrough="quick-request"]',
    title: 'Quick Product Request',
    content: 'Use the Quick Request feature to create products using natural language. Try something like: "Create a product for a mobile banking app" and click Process. Once you\'ve created your product, you can resume this walkthrough anytime from the Help page.',
    placement: 'bottom',
    action: 'observe',
    highlight: true,
  },
  // User Stories Quick Request
  {
    id: 'navigate-user-stories',
    targetSelector: '[data-walkthrough="nav-user-stories"]',
    title: 'Define User Stories',
    content: 'After creating your product, navigate to User Stories. You can use the Agent Sidebar to ask the Product Manager agent to "define user stories for my new product".',
    placement: 'right',
    action: 'navigate',
    navigateTo: '/user-stories',
    highlight: true,
    agentPrompt: 'Define user stories for my new product',
  },
  // Quick User Story Request
  {
    id: 'quick-story-request',
    targetSelector: '[data-walkthrough="quick-request"]',
    title: 'Quick User Story Request',
    content: 'You can also use the Quick Request here to create user stories. Try: "Create a user story for user login functionality".',
    placement: 'bottom',
    action: 'observe',
    highlight: true,
  },
  // Navigate to Releases
  {
    id: 'navigate-releases',
    targetSelector: '[data-walkthrough="nav-releases"]',
    title: 'Plan Your MVP Release',
    content: 'Now let\'s plan your MVP! Navigate to Releases to create your first release milestone.',
    placement: 'right',
    action: 'navigate',
    navigateTo: '/releases',
    highlight: true,
  },
  // Create MVP Release
  {
    id: 'create-release',
    targetSelector: '[data-walkthrough="quick-request"]',
    title: 'Create MVP Release',
    content: 'Use the Agent Sidebar to ask: "Create an MVP release for my product with the user stories we defined". The Product Manager will help you plan your first release!',
    placement: 'bottom',
    action: 'observe',
    highlight: true,
    agentPrompt: 'Create an MVP release for my product with the user stories we defined',
  },
  // Final step
  {
    id: 'walkthrough-complete',
    targetSelector: '[data-walkthrough="sidebar-nav"]',
    title: '🎉 You\'re Ready!',
    content: 'Congratulations! You now know the basics of Velocity. Use the Agent Sidebar anytime to get AI assistance with your product development workflow. Happy building!',
    placement: 'right',
    action: 'observe',
    highlight: false,
  },
];

const DEFAULT_PREREQUISITES: PrerequisiteCheck[] = [
  {
    id: 'cursor',
    label: 'Cursor IDE',
    description: 'AI-first code editor for agentic code generation. Download from cursor.com',
    isRequired: false, // Strongly recommended, not required
    isCompleted: false,
  },
  {
    id: 'claude-code',
    label: 'Claude Code',
    description: 'Anthropic\'s agentic coding assistant. Download from docs.anthropic.com',
    isRequired: false,
    isCompleted: false,
  },
  {
    id: 'cursor-agent',
    label: 'Cursor CLI',
    description: 'Command-line interface for Cursor IDE. Install from Cursor\'s Settings menu.',
    isRequired: false,
    isCompleted: false,
  },
];

const WalkthroughContext = createContext<WalkthroughContextType | undefined>(undefined);

export function WalkthroughProvider({ children }: { children: React.ReactNode }) {
  const skillShopServiceRef = useRef<SkillShopService | null>(null);
  const integrationSettingsRef = useRef<IntegrationSettings | null>(null);
  const hasCompletedWalkthroughRef = useRef<boolean>(false);
  const firstVisitCheckTriggeredRef = useRef<boolean>(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize the SkillShopService
  if (!skillShopServiceRef.current) {
    skillShopServiceRef.current = new SkillShopService();
  }

  const [state, setState] = useState<WalkthroughState>({
    isActive: false,
    currentStepIndex: 0,
    showPrerequisites: false,
    prerequisitesCompleted: false,
    prerequisites: DEFAULT_PREREQUISITES,
    hasCompletedWalkthrough: false,
  });

  // Load walkthrough state from user settings on mount
  useEffect(() => {
    const loadWalkthroughState = async () => {
      const service = skillShopServiceRef.current;
      if (!service) return;

      let hasCompletedWalkthrough = false;
      
      try {
        const userSettings = await service.getUserSettings();
        integrationSettingsRef.current = userSettings?.integrationSettings || null;
        const savedState = userSettings?.integrationSettings?.productApp?.walkthroughState;
        
        if (savedState) {
          // Merge saved prerequisites with defaults to handle new prerequisites added later
          const mergedPrerequisites = DEFAULT_PREREQUISITES.map(defaultPreq => {
            const savedPreq = savedState.prerequisites.find(p => p.id === defaultPreq.id);
            return savedPreq 
              ? { ...defaultPreq, isCompleted: savedPreq.isCompleted }
              : defaultPreq;
          });

          hasCompletedWalkthrough = savedState.hasCompletedWalkthrough || false;
          
          setState(prev => ({
            ...prev,
            prerequisitesCompleted: savedState.prerequisitesCompleted || false,
            prerequisites: mergedPrerequisites,
            hasCompletedWalkthrough,
          }));
        }
      } catch (error) {
        console.error('[Walkthrough] Failed to load walkthrough state:', error);
      } finally {
        setIsInitialized(true);
        
        // Store loaded hasCompletedWalkthrough value in ref for first-visit check
        // This avoids race conditions with React state updates
        hasCompletedWalkthroughRef.current = hasCompletedWalkthrough;
      }
    };

    loadWalkthroughState();
  }, []);

  // Persist relevant state to user settings when it changes
  useEffect(() => {
    // Don't save until we've loaded initial state
    if (!isInitialized) return;

    const saveState = async () => {
      const service = skillShopServiceRef.current;
      if (!service) return;

      const stateToSave: StoredWalkthroughState = {
        prerequisitesCompleted: state.prerequisitesCompleted,
        prerequisites: state.prerequisites.map(p => ({ id: p.id, isCompleted: p.isCompleted })),
        hasCompletedWalkthrough: state.hasCompletedWalkthrough,
      };

      try {
        await service.saveWalkthroughState(stateToSave, integrationSettingsRef.current);
      } catch (error) {
        console.error('[Walkthrough] Failed to save walkthrough state:', error);
      }
    };

    saveState();
  }, [state.prerequisitesCompleted, state.prerequisites, state.hasCompletedWalkthrough, isInitialized]);

  const currentStep = state.isActive ? WALKTHROUGH_STEPS[state.currentStepIndex] || null : null;

  const startWalkthrough = useCallback(() => {
    // Show the prerequisites dialog to suggest tools (but don't require them)
    if (!state.prerequisitesCompleted) {
      setState(prev => ({ ...prev, showPrerequisites: true }));
    } else {
      // Start the walkthrough directly if user has already seen prerequisites
      setState(prev => ({
        ...prev,
        isActive: true,
        currentStepIndex: 0,
        showPrerequisites: false,
      }));
    }
  }, [state.prerequisitesCompleted]);

  const endWalkthrough = useCallback(() => {
    hasCompletedWalkthroughRef.current = true;
    setState(prev => ({
      ...prev,
      isActive: false,
      hasCompletedWalkthrough: true,
    }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const nextIndex = prev.currentStepIndex + 1;
      if (nextIndex >= WALKTHROUGH_STEPS.length) {
        hasCompletedWalkthroughRef.current = true;
        return {
          ...prev,
          isActive: false,
          hasCompletedWalkthrough: true,
        };
      }
      return {
        ...prev,
        currentStepIndex: nextIndex,
      };
    });
  }, []);

  const previousStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStepIndex: Math.max(0, prev.currentStepIndex - 1),
    }));
  }, []);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < WALKTHROUGH_STEPS.length) {
      setState(prev => ({
        ...prev,
        currentStepIndex: index,
      }));
    }
  }, []);

  const skipWalkthrough = useCallback(() => {
    hasCompletedWalkthroughRef.current = true;
    setState(prev => ({
      ...prev,
      isActive: false,
      showPrerequisites: false,
      hasCompletedWalkthrough: true,
    }));
  }, []);

  const showPrerequisitesDialog = useCallback(() => {
    setState(prev => ({ ...prev, showPrerequisites: true }));
  }, []);

  const hidePrerequisitesDialog = useCallback(() => {
    // When user dismisses the dialog (closes window), mark as completed so it won't auto-show again
    hasCompletedWalkthroughRef.current = true;
    setState(prev => ({ ...prev, showPrerequisites: false, hasCompletedWalkthrough: true }));
  }, []);

  const updatePrerequisite = useCallback((id: string, isCompleted: boolean) => {
    setState(prev => ({
      ...prev,
      prerequisites: prev.prerequisites.map(p =>
        p.id === id ? { ...p, isCompleted } : p
      ),
    }));
  }, []);

  const completePrerequisites = useCallback(() => {
    setState(prev => ({
      ...prev,
      prerequisitesCompleted: true,
      showPrerequisites: false,
      isActive: true,
      currentStepIndex: 0,
    }));
  }, []);

  const resetWalkthrough = useCallback(async () => {
    const newState = {
      isActive: false,
      currentStepIndex: 0,
      showPrerequisites: false,
      prerequisitesCompleted: false,
      prerequisites: DEFAULT_PREREQUISITES,
      hasCompletedWalkthrough: false,
    };
    
    setState(newState);
    hasCompletedWalkthroughRef.current = false;
    firstVisitCheckTriggeredRef.current = false;
    
    // Clear from user settings
    const service = skillShopServiceRef.current;
    if (service) {
      const stateToSave: StoredWalkthroughState = {
        prerequisitesCompleted: false,
        prerequisites: DEFAULT_PREREQUISITES.map(p => ({ id: p.id, isCompleted: false })),
        hasCompletedWalkthrough: false,
      };
      try {
        await service.saveWalkthroughState(stateToSave, integrationSettingsRef.current);
      } catch (error) {
        console.error('[Walkthrough] Failed to reset walkthrough state:', error);
      }
    }
  }, []);

  // Trigger the first-visit check
  // Uses ref to avoid race conditions with React state updates
  const triggerFirstVisitCheck = useCallback(() => {
    if (!isInitialized) return;
    if (firstVisitCheckTriggeredRef.current) return; // Only check once
    
    firstVisitCheckTriggeredRef.current = true;
    
    // Use the ref value which was set during loading, avoiding state race conditions
    if (!hasCompletedWalkthroughRef.current && !state.isActive) {
      setState(prev => ({ ...prev, showPrerequisites: true }));
    }
  }, [isInitialized, state.isActive]);

  // Automatically trigger first-visit check after initialization
  useEffect(() => {
    if (!isInitialized) return;
    
    // Small delay to ensure the app is fully rendered
    const timer = setTimeout(() => {
      triggerFirstVisitCheck();
    }, 500);
    return () => clearTimeout(timer);
  }, [isInitialized, triggerFirstVisitCheck]);

  const value: WalkthroughContextType = {
    state,
    steps: WALKTHROUGH_STEPS,
    currentStep,
    isInitialized,
    startWalkthrough,
    endWalkthrough,
    nextStep,
    previousStep,
    goToStep,
    skipWalkthrough,
    showPrerequisitesDialog,
    hidePrerequisitesDialog,
    updatePrerequisite,
    completePrerequisites,
    resetWalkthrough,
    triggerFirstVisitCheck,
  };

  return (
    <WalkthroughContext.Provider value={value}>
      {children}
    </WalkthroughContext.Provider>
  );
}

export function useWalkthrough() {
  const context = useContext(WalkthroughContext);
  if (context === undefined) {
    throw new Error('useWalkthrough must be used within a WalkthroughProvider');
  }
  return context;
}

